import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Grid,
  Typography,
  Card,
  CardContent,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  Button,
  Divider,
  Tooltip,
  Link,
} from '@mui/material';
import {
  Work as WorkIcon,
  AttachMoney as MoneyIcon,
  Event as EventIcon,
  EventAvailable as ScheduleIcon,
  CheckCircle as CompletedIcon,
  Repeat as RepeatIcon,
  CreditCard as CardIcon,
  Star as StarIcon,
  Hotel as SmoobuIcon,
  RequestQuote as QuoteIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import logger from '../../utils/logger';
import SendInvoiceButton from './SendInvoiceButton';

const SERVICE_TYPE_LABELS = {
  regular: 'Regular',
  deep_clean: 'Deep Clean',
  move_in: 'Move In',
  move_out: 'Move Out',
  one_time: 'One Time',
  post_construction: 'Post Construction',
};

const money = (cents) => `$${((cents || 0) / 100).toFixed(2)}`;
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString() : '—');
const fmtDateTime = (d) =>
  d ? new Date(d).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }) : '—';
const daysAgo = (d) => (d ? Math.floor((Date.now() - new Date(d).getTime()) / 86400000) : null);

const jobStatusColor = (status) => {
  switch (status) {
    case 'COMPLETED': return 'success';
    case 'IN_PROGRESS': return 'info';
    case 'CANCELLED': return 'error';
    default: return 'warning';
  }
};

const StatCard = ({ icon, label, value, caption, color = 'primary' }) => (
  <Card variant="outlined" sx={{ height: '100%' }}>
    <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
      <Box display="flex" alignItems="center" gap={1} mb={0.5}>
        {icon}
        <Typography variant="caption" color="text.secondary">{label}</Typography>
      </Box>
      <Typography variant="h5" color={`${color}.main`} fontWeight="bold">{value}</Typography>
      {caption && <Typography variant="caption" color="text.secondary">{caption}</Typography>}
    </CardContent>
  </Card>
);

const JobsTable = ({ jobs, emptyText, showCompleted }) => {
  if (!jobs.length) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
        {emptyText}
      </Typography>
    );
  }
  return (
    <TableContainer component={Paper} variant="outlined">
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell><strong>Job #</strong></TableCell>
            <TableCell><strong>When</strong></TableCell>
            <TableCell><strong>Service</strong></TableCell>
            <TableCell><strong>Source</strong></TableCell>
            <TableCell align="right"><strong>Amount</strong></TableCell>
            <TableCell><strong>Status</strong></TableCell>
            <TableCell><strong>Technicians</strong></TableCell>
            <TableCell align="right" />
          </TableRow>
        </TableHead>
        <TableBody>
          {jobs.map((job) => (
            <TableRow key={job.id} hover>
              <TableCell>{job.jobNumber || job.id.slice(0, 8)}</TableCell>
              <TableCell>
                <Tooltip title={job.address ? [job.address.street, job.address.city].filter(Boolean).join(', ') : ''}>
                  <span>{fmtDateTime(job.scheduledDate)}</span>
                </Tooltip>
              </TableCell>
              <TableCell>
                {SERVICE_TYPE_LABELS[job.serviceType] || job.serviceType || '—'}
                <Typography variant="caption" color="text.secondary" display="block">
                  {job.jobType?.replace('_', ' ').toLowerCase()} · {job.estimatedDuration} min
                </Typography>
              </TableCell>
              <TableCell><Chip label={job.sourceSystem} size="small" variant="outlined" /></TableCell>
              <TableCell align="right">{job.totalAmount != null ? money(job.totalAmount) : '—'}</TableCell>
              <TableCell><Chip label={job.status} color={jobStatusColor(job.status)} size="small" /></TableCell>
              <TableCell>
                {job.technicians?.length ? (
                  <Box display="flex" flexWrap="wrap" gap={0.5}>
                    {job.technicians.map((t) => (
                      <Chip key={t.id} label={t.name} size="small" variant="outlined" color={t.role === 'LEAD' ? 'primary' : 'default'} />
                    ))}
                  </Box>
                ) : (
                  <Typography variant="caption" color={showCompleted ? 'text.secondary' : 'warning.main'}>
                    {showCompleted ? '—' : 'Unassigned'}
                  </Typography>
                )}
              </TableCell>
              <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                <SendInvoiceButton jobId={job.id} jobNumber={job.jobNumber} compact disabled={job.status === 'CANCELLED'} />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

/**
 * Admin 360° view of one customer: stats, upcoming and past jobs, recurring
 * plans, quotes, payments and reviews. Fed by GET /api/users/clients/:id/overview.
 */
const CustomerOverviewPanel = ({ customerId, onScheduleJob, refreshKey = 0 }) => {
  const apiBase = process.env.REACT_APP_API_URL?.replace(/\/$/, '') || '';
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    if (!customerId) return;
    setLoading(true);
    setError('');
    try {
      const response = await fetch(`${apiBase}/api/users/clients/${customerId}/overview`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      const json = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(json.error || 'Failed to load customer overview');
      setData(json);
    } catch (err) {
      logger.error('[CustomerOverviewPanel] load failed:', err);
      setError(err.message || 'Failed to load customer overview');
    } finally {
      setLoading(false);
    }
  }, [apiBase, customerId]);

  useEffect(() => { load(); }, [load, refreshKey]);

  if (loading && !data) {
    return <Box display="flex" justifyContent="center" py={6}><CircularProgress /></Box>;
  }
  if (error) {
    return <Alert severity="error" action={<Button onClick={load}>Retry</Button>}>{error}</Alert>;
  }
  if (!data) return null;

  const { customer, stats, upcomingJobs, pastJobs, recurringSchedules, quotations, payments, reviews, smoobuAccounts } = data;
  const sinceLast = daysAgo(stats.lastCompletedJobDate);
  const activePlans = recurringSchedules.filter((r) => r.status === 'ACTIVE');
  const problemPlans = recurringSchedules.filter((r) => r.status === 'PAST_DUE' || r.failureCount > 0);
  const openQuotes = quotations.filter((q) => ['PENDING', 'SENT', 'ACTIVE'].includes(q.status));
  const unassignedUpcoming = upcomingJobs.filter((j) => !j.technicians?.length);

  return (
    <Box>
      {/* Alerts worth knowing */}
      {(problemPlans.length > 0 || unassignedUpcoming.length > 0 || !customer.address?.street) && (
        <Box mb={2} display="flex" flexDirection="column" gap={1}>
          {problemPlans.length > 0 && (
            <Alert severity="error" icon={<WarningIcon />}>
              Recurring billing has failed for this customer ({problemPlans[0].lastChargeError || 'past due'}).
            </Alert>
          )}
          {unassignedUpcoming.length > 0 && (
            <Alert severity="warning">
              {unassignedUpcoming.length} upcoming job{unassignedUpcoming.length > 1 ? 's have' : ' has'} no technician assigned.
            </Alert>
          )}
          {!customer.address?.street && (
            <Alert severity="info">No service address on file. Add one in the Profile tab before scheduling.</Alert>
          )}
        </Box>
      )}

      {/* Stats */}
      <Grid container spacing={1.5} mb={2}>
        <Grid item xs={6} sm={4} md={2}>
          <StatCard icon={<WorkIcon fontSize="small" color="primary" />} label="Total jobs" value={stats.totalJobs}
            caption={stats.firstJobDate ? `since ${fmtDate(stats.firstJobDate)}` : null} />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <StatCard icon={<CompletedIcon fontSize="small" color="success" />} label="Completed" value={stats.completedJobs} color="success"
            caption={stats.cancelledJobs ? `${stats.cancelledJobs} cancelled` : null} />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <StatCard icon={<EventIcon fontSize="small" color="info" />} label="Upcoming" value={stats.upcomingJobs} color="info"
            caption={stats.nextJobDate ? `next ${fmtDate(stats.nextJobDate)}` : 'nothing scheduled'} />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <StatCard icon={<MoneyIcon fontSize="small" color="success" />} label="Lifetime value" value={money(stats.lifetimeValueCents)} color="success"
            caption={stats.completedJobs ? `avg ${money(stats.averageJobValueCents)}` : null} />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <StatCard icon={<EventIcon fontSize="small" color="action" />} label="Last service"
            value={sinceLast == null ? '—' : sinceLast === 0 ? 'Today' : `${sinceLast}d ago`}
            color={sinceLast != null && sinceLast > 60 ? 'warning' : 'text'}
            caption={fmtDate(stats.lastCompletedJobDate)} />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <StatCard icon={<StarIcon fontSize="small" color="warning" />} label="Rating given"
            value={stats.averageRatingGiven != null ? `${stats.averageRatingGiven} / 5` : '—'} color="warning"
            caption={reviews.length ? `${reviews.length} review${reviews.length > 1 ? 's' : ''}` : 'no reviews yet'} />
        </Grid>
      </Grid>

      {/* Quick facts */}
      <Box display="flex" flexWrap="wrap" gap={1} mb={2} alignItems="center">
        <Chip size="small" icon={<EventIcon />} label={`Customer since ${fmtDate(customer.customerSince)}`} variant="outlined" />
        <Chip size="small" icon={<CardIcon />} label={customer.hasSavedCard ? 'Card on file' : 'No card on file'} color={customer.hasSavedCard ? 'success' : 'default'} variant="outlined" />
        {activePlans.map((r) => (
          <Chip key={r.id} size="small" icon={<RepeatIcon />} color="primary" variant="outlined"
            label={`${r.frequency} plan · ${money(r.amountCents)} · next ${fmtDate(r.nextVisitDate)}`} />
        ))}
        {smoobuAccounts.map((a) => (
          <Chip key={a.id} size="small" icon={<SmoobuIcon />} label={`Smoobu: ${a.accountName}`} variant="outlined" />
        ))}
        {openQuotes.length > 0 && (
          <Chip size="small" icon={<QuoteIcon />} color="info" variant="outlined"
            label={`${openQuotes.length} open quote${openQuotes.length > 1 ? 's' : ''}`} />
        )}
        {stats.frequentTechnicians?.length > 0 && (
          <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
            Usual technicians: {stats.frequentTechnicians.map((t) => `${t.name} (${t.jobs})`).join(', ')}
          </Typography>
        )}
      </Box>

      {/* Upcoming jobs */}
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
        <Typography variant="subtitle1" fontWeight="bold">Upcoming jobs</Typography>
        {onScheduleJob && (
          <Button size="small" variant="contained" startIcon={<ScheduleIcon />} onClick={onScheduleJob}
            disabled={!customer.clientProfileId}>
            Schedule Job
          </Button>
        )}
      </Box>
      <JobsTable jobs={upcomingJobs} emptyText="Nothing scheduled. Use Schedule Job to book the next visit." />

      <Divider sx={{ my: 2 }} />

      {/* Past jobs */}
      <Typography variant="subtitle1" fontWeight="bold" mb={1}>Past jobs</Typography>
      <JobsTable jobs={pastJobs} emptyText="No past jobs." showCompleted />

      {/* Quotes / payments / reviews */}
      {(quotations.length > 0 || payments.length > 0 || reviews.length > 0) && <Divider sx={{ my: 2 }} />}

      <Grid container spacing={2}>
        {quotations.length > 0 && (
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" fontWeight="bold" mb={1}>Quotes</Typography>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead><TableRow><TableCell>Date</TableCell><TableCell>Property</TableCell><TableCell align="right">Price</TableCell><TableCell>Status</TableCell></TableRow></TableHead>
                <TableBody>
                  {quotations.map((q) => (
                    <TableRow key={q.id}>
                      <TableCell>{fmtDate(q.createdAt)}</TableCell>
                      <TableCell>{[q.propertyType, q.bedrooms && `${q.bedrooms}bd`, q.bathrooms && `${q.bathrooms}ba`].filter(Boolean).join(' · ') || '—'}</TableCell>
                      <TableCell align="right">{q.price != null ? `$${Number(q.price).toFixed(2)}` : '—'}</TableCell>
                      <TableCell><Chip size="small" label={q.status} color={q.status === 'ACCEPTED' ? 'success' : q.status === 'REJECTED' || q.status === 'EXPIRED' ? 'default' : 'info'} /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
        )}

        {payments.length > 0 && (
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" fontWeight="bold" mb={1}>Payments</Typography>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead><TableRow><TableCell>Date</TableCell><TableCell>Method</TableCell><TableCell align="right">Amount</TableCell><TableCell>Status</TableCell></TableRow></TableHead>
                <TableBody>
                  {payments.map((p) => (
                    <TableRow key={p.id}>
                      <TableCell>{fmtDate(p.createdAt)}</TableCell>
                      <TableCell>{[p.paymentMethod, p.cardBrand].filter(Boolean).join(' · ') || '—'}</TableCell>
                      <TableCell align="right">
                        {p.receiptUrl ? <Link href={p.receiptUrl} target="_blank" rel="noopener">{money(p.amountCents)}</Link> : money(p.amountCents)}
                      </TableCell>
                      <TableCell><Chip size="small" label={p.status} color={/succe|paid|captured/i.test(p.status || '') ? 'success' : /fail|refund/i.test(p.status || '') ? 'error' : 'default'} /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
        )}

        {reviews.length > 0 && (
          <Grid item xs={12}>
            <Typography variant="subtitle2" fontWeight="bold" mb={1}>Reviews left by this customer</Typography>
            {reviews.map((r) => (
              <Box key={r.id} display="flex" gap={1} alignItems="flex-start" mb={0.5}>
                <Chip size="small" icon={<StarIcon />} label={`${r.rating}/5`} color="warning" variant="outlined" />
                <Typography variant="body2">{r.comment || <em>no comment</em>}</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto', whiteSpace: 'nowrap' }}>{fmtDate(r.createdAt)}</Typography>
              </Box>
            ))}
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

export default CustomerOverviewPanel;
